import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
} from '@mui/material';
import {useGetProductionPreviewFile} from '../../../hooks/productions/useProductions';
import '../Demo.scss';

const ProductionPreviewFileApiTest: React.FC = () => {
  const [filePath, setFilePath] = useState<string>(
    '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\Productions\\dndProject\\CADE5C9F-B105-4A5A-ACA2-AFBD3096E55E\\Temp\\Production314\\0.pdf'
  );

  const [downloadResult, setDownloadResult] = useState<string>('');

  const downloadMutation = useGetProductionPreviewFile(
    (data) => {
      setDownloadResult(
        `✅ Download successful! File: ${data.filename}, Content-Type: ${data.contentType}`
      );
    },
    (error) => {
      setDownloadResult(`❌ Download failed: ${error.message}`);
    }
  );

  const handleDownload = () => {
    downloadMutation.mutate({
      filePath,
    });
  };

  const handleReset = () => {
    setDownloadResult('');
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          📥 Production Preview File API Demo
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Test the download production preview file endpoint to download preview
          files (binary content)
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="GET" color="primary" size="small" data-testid={`production-preview-file-chip-info`} />
            <Typography variant="h6" className="typo-text subhead2_size">
              Download Production Preview File
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/preview/file
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Download production preview file (binary content) with query
            parameter filePath. Returns file with content-disposition and
            content-type headers.
          </Typography>
        </CardContent>
      </Card>
      {/* Query Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Query Parameters
          </Typography>

          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <TextField
              label="File Path"
              value={filePath}
              onChange={(e) => setFilePath(e.target.value)}
              fullWidth
              size="small"
              placeholder="\\\\server\\path\\to\\file.pdf"
              multiline
              rows={3}
            />
            <Typography
              className="typo-text body3_size"
              sx={{color: '#666', fontStyle: 'italic'}}
            >
              Note: The file path will be URL encoded automatically
            </Typography>
          </Box>
        </CardContent>
      </Card>
      {/* Actions */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box sx={{display: 'flex', gap: 2, marginBottom: 2}}>
            <Button
              variant="contained"
              onClick={handleDownload}
              disabled={downloadMutation.isPending}
              sx={{minWidth: 120}}
              data-testid="production-preview-file-api-test-button">
              {downloadMutation.isPending ? 'Downloading...' : 'Download File'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={downloadMutation.isPending}
              data-testid="production-preview-file-api-test-reset-button">
              Reset
            </Button>
          </Box>

          {downloadMutation.isPending && (
            <Box sx={{width: '100%', marginTop: 2}}>
              <LinearProgress />
              <Typography
                className="typo-text body3_size"
                sx={{marginTop: 1, color: '#666'}}
              >
                Downloading file...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      {/* Result */}
      {downloadResult && (
        <Card>
          <CardContent>
            <Alert
              severity={downloadResult.includes('✅') ? 'success' : 'error'}
            >
              <Typography className="typo-text body2_size">
                {downloadResult}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Example */}
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Example
          </Typography>
          <Box
            sx={{
              backgroundColor: '#f5f5f5',
              padding: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '12px',
            }}
          >
            <Typography className="typo-text body3_size" component="pre">
              {`GET /productions/api/v1/production/preview/file?filePath=\\\\server\\path\\to\\file.pdf

Response Headers:
  content-disposition: attachment; filename="file.pdf"
  content-type: application/pdf

Response Body:
  [Binary file content]`}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductionPreviewFileApiTest;
